import { THEME } from '@Theme';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useCheckDomainAvailability } from '../../../../../queries/Admin/Newsletter/useCheckDomainAvailability';
import { useRequestEmailDomain } from '../../../../../queries/Admin/Email/useRequestEmailDomain';
import { useSuggestedEmailDomain } from '../../../../../queries/Admin/Email/useSuggestedEmailDomain';
import { Button } from '../../../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogPopup,
	DialogTitle,
} from '../../../../../web/components-v2/Dialog';
import { Input } from '../../../../../web/components-v2/Input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../../web/components-v2/Popover';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { InfoIcon } from '../../../../../web/components/svgs/icons/InfoIcon';
import { SuccessCheckIcon } from '../../../../../web/components/svgs/icons/SuccessCheckIcon';
import { XIcon } from '../../../../../web/components/svgs/icons/XIcon';
import { emailDomainRequestDialogStyles as styleSheet } from './emailDomainRequestDialogStyles';

export interface IEmailDomainRequestDialogProps {
	open: boolean;
	impersonationContext: Api.IImpersonationContext;
	features?: Api.IAccountFeatures;
	onClose: () => void;
	/** Called after a successful registration so the parent can refetch the account. */
	onDomainRegistered: () => void;
}

/**
 * Suggests a send-domain, lets the admin edit it, checks availability, then registers it. No viewmodels: the
 * plain `impersonationContext` object is passed straight to the three react-query hooks. Mutations surface errors via `toast` because
 * there is no global MutationCache.
 */
export const EmailDomainRequestDialog: React.FC<IEmailDomainRequestDialogProps> = ({
	open,
	impersonationContext,
	features,
	onClose,
	onDomainRegistered,
}) => {
	const existingDomainName = features?.sendEmail?.domainName;

	const [domainName, setDomainName] = React.useState('');
	const [domainNameAvailability, setDomainNameAvailability] = React.useState<boolean | null>(null);
	const [isDomainNameDisabled, setIsDomainNameDisabled] = React.useState(false);
	const [infoIsOpen, setInfoIsOpen] = React.useState(false);
	// The suggested (or already-registered) domain, restored by "Use Different Domain".
	const domainNameRef = React.useRef('');

	const domainNameQuery = useSuggestedEmailDomain({
		enabled: !existingDomainName,
		impersonationContext,
	});

	const registerDomainMutation = useRequestEmailDomain({
		impersonationContext,
		onError: error => {
			toast.error(error?.systemMessage ?? 'Failed to register email domain.');
		},
		onSuccess: data => {
			const registeredName = data.name ?? '';
			setDomainName(registeredName);
			setDomainNameAvailability(null);
			setIsDomainNameDisabled(true);
			domainNameRef.current = registeredName;
			toast.success('Email domain registered successfully!');
			onDomainRegistered();
			onClose();
		},
	});

	const checkAvailabilityMutation = useCheckDomainAvailability({
		impersonationContext,
		onError: error => {
			toast.error(error?.systemMessage ?? 'Failed to check domain availability.');
			setDomainNameAvailability(false);
		},
		onSuccess: data => {
			setDomainNameAvailability(data);
		},
	});

	const isRegistering = registerDomainMutation.isPending;

	// Seed the input with the suggested domain once it loads (only if the admin hasn't typed yet).
	React.useEffect(() => {
		if (domainNameQuery.data && !domainName) {
			setDomainName(domainNameQuery.data);
			setIsDomainNameDisabled(false);
			domainNameRef.current = domainNameQuery.data;
		}
	}, [domainNameQuery.data, domainName]);

	// An already-registered domain is shown read-only.
	React.useEffect(() => {
		if (existingDomainName) {
			setDomainName(existingDomainName);
			setIsDomainNameDisabled(true);
			domainNameRef.current = existingDomainName;
		}
	}, [existingDomainName]);

	const handleSave = () => {
		if (!domainNameAvailability) {
			toast.error('Please check domain name availability before registering.');
			return;
		}
		registerDomainMutation.mutate({ domainName });
	};

	const handleDomainNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setDomainName(e.target.value.split(' ').join('-'));
		setDomainNameAvailability(null);
	};

	const handleCheckAvailability = () => {
		checkAvailabilityMutation.mutate({ domainName });
	};

	const handleReset = () => {
		setDomainName(domainNameRef.current);
		setDomainNameAvailability(null);
		setIsDomainNameDisabled(false);
	};

	const isLoadingSuggestion = domainNameQuery.isPending && domainNameQuery.isFetching;
	const canReset = !!domainNameQuery.data && isDomainNameDisabled;

	return (
		<Dialog open={open} onOpenChange={o => !o && onClose()} dismissOnOutsideClick={false}>
			<DialogPopup styles={[styleSheet.dialogPopup]}>
				<DialogTitle>Request Email Domain</DialogTitle>
				<DialogBody>
					<DialogDescription styles={[styleSheet.description]}>
						Configure a dedicated email domain for sending emails from Levitate&apos;s servers. This allows accounts
						with consumer email providers (Gmail, Yahoo, etc.) to send group emails.
					</DialogDescription>

					{isLoadingSuggestion ? (
						<div className={css(styleSheet.loaderWrap)}>
							<LoadingSpinner />
						</div>
					) : (
						<div className={css(styleSheet.domainNameWrap)}>
							<div className={css(styleSheet.domainNameInputWrap)}>
								<label className={css(styleSheet.labelRow)} htmlFor='domainName'>
									<span>Domain Name</span>
									<span className={css(styleSheet.errorRequired)}>*</span>
									{!existingDomainName ? (
										<Popover open={infoIsOpen} onOpenChange={setInfoIsOpen}>
											<PopoverTrigger
												type='button'
												className={css(styleSheet.infoTrigger)}
												aria-label='Domain availability info'
												onMouseEnter={() => setInfoIsOpen(true)}
											>
												<InfoIcon fillColor={THEME.colors.primary.main} />
											</PopoverTrigger>
											<PopoverContent styles={[styleSheet.infoContent]} side='top' align='center'>
												<p className={css(styleSheet.infoMessage)}>
													Domain name availability is not guaranteed. A domain name availability check is needed before
													the domain name can be registered.
												</p>
											</PopoverContent>
										</Popover>
									) : null}
								</label>
								<div className={css(styleSheet.inputWrap)}>
									<Input
										type='text'
										id='domainName'
										value={domainName}
										placeholder='example.com'
										styles={
											domainNameAvailability === false ? [styleSheet.input, styleSheet.inputError] : [styleSheet.input]
										}
										disabled={isDomainNameDisabled}
										onChange={handleDomainNameChange}
									/>
									{domainNameAvailability === true ? (
										<figure className={css(styleSheet.availabilityIcon)}>
											<SuccessCheckIcon />
										</figure>
									) : null}
									{domainNameAvailability === false ? (
										<figure className={css(styleSheet.availabilityIcon)}>
											<XIcon width={12} height={12} color={THEME.colors.danger.main} />
										</figure>
									) : null}
								</div>
							</div>
							{!existingDomainName ? (
								<div className={css(styleSheet.availabilityActions)}>
									{canReset ? (
										<Button variant='link' onClick={handleReset} disabled={isRegistering}>
											Use Different Domain
										</Button>
									) : null}
									<Button variant='link' onClick={handleCheckAvailability} disabled={!domainName || isRegistering}>
										Check Availability
									</Button>
								</div>
							) : null}
						</div>
					)}
				</DialogBody>

				<DialogFooter>
					<Button variant='secondary' onClick={onClose} disabled={isRegistering}>
						Cancel
					</Button>
					{!existingDomainName ? (
						<Button
							variant='primary'
							onClick={handleSave}
							disabled={!domainNameAvailability || isRegistering}
							isLoading={isRegistering}
						>
							Register Domain
						</Button>
					) : null}
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
